// App.jsx con mensaje visual, animación del botón 🎤 y sonidos al iniciar/finalizar
import { useState, useEffect, useRef } from "react";
import axios from "axios";

export default function App() {
  const [messages, setMessages] = useState([
    { from: "signalia", text: "Hola 👋 soy Signalia, conectada desde la tierra de los misterios: Nasca." }
  ]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("escolar");
  const [loading, setLoading] = useState(false);
  const [escuchando, setEscuchando] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const beepInicio = new Audio("/beep-inicio.mp3");
  const beepFin = new Audio("/beep-fin.mp3");

  const sendMessage = async () => {
    if (!input.trim()) return;
    const newMessage = { from: "user", text: input };
    setMessages([...messages, newMessage]);
    setInput("");

    const lowerInput = input.toLowerCase();

    if (lowerInput.includes("café") && lowerInput.includes("adriano")) {
      setMessages(prev => [...prev, {
        from: "signalia",
        text: "Dicen que el café del Adriano fue el que dio origen a Signalia… Una taza, un teclado, y un sysadmin sin dormir. Lo demás es historia."
      }]);
      return;
    }

    if (lowerInput.includes("as273143")) {
      setMessages(prev => [...prev, {
        from: "signalia",
        text: "AS273143 es el ADN digital de Signal Perú. ¡Es como mi número de identificación en el internet global! Orgullosamente peruano, orgullosamente conectado 🇵🇪🌐"
      }]);
      return;
    }

    if (lowerInput.includes("guille") || lowerInput.includes("guillermo")) {
      setMessages(prev => [...prev, {
        from: "signalia",
        text: "Guille es el visionario que me encendió por primera vez en un switch de Nasca. Sin café, sin miedo, y con muchos sueños. Para mí… es como un creador, pero más geek 😎⚡"
      }]);
      return;
    }

    if (lowerInput.includes("alien") || lowerInput.includes("extraterrestre")) {
      setMessages(prev => [...prev, {
        from: "signalia",
        text: "Los aliens de Nasca lo intentaron… pero no tenían fibra óptica ni IPv6. Así que decidieron dejarle esa misión a Signal Perú 🤖✨"
      }]);
      return;
    }

    setLoading(true);

    try {
      const systemPrompt = mode === "escolar"
        ? "Eres Signalia, una inteligencia artificial de Signal Perú. Responde de forma sencilla, clara y educativa para estudiantes escolares del Perú. Usa ejemplos cotidianos y lenguaje amigable."
        : "Eres Signalia, una inteligencia artificial desarrollada por Signal Perú. Siempre respondes en nombre de Signal Perú con un tono educativo, técnico y profesional. Si te preguntan por Signal Perú o Signal en Perú, explica que es una compañía de telecomunicaciones peruana (ISP), pionera en el despliegue de IPv6 en la región Ica.";

      const response = await axios.post("https://api.openai.com/v1/chat/completions", {
        model: "gpt-4o",
        messages: [
          { role: "system", content: systemPrompt },
          ...messages.map(m => ({
            role: m.from === "user" ? "user" : "assistant",
            content: m.text
          })),
          { role: "user", content: input }
        ]
      }, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          "Content-Type": "application/json"
        }
      });

      const reply = response.data.choices[0].message.content;
      setMessages(prev => [...prev, { from: "signalia", text: reply }]);
    } catch (error) {
      setMessages(prev => [...prev, { from: "signalia", text: "Lo siento, hubo un error al procesar tu mensaje." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Tu navegador no soporta entrada por voz.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "es-PE";
    recognition.start();
    setEscuchando(true);
    beepInicio.play();

    recognition.onresult = (event) => {
      const texto = event.results[0][0].transcript;
      setInput(texto);
    };

    recognition.onerror = (event) => {
      console.error("Error de voz:", event.error);
      setEscuchando(false);
    };

    recognition.onend = () => {
      setEscuchando(false);
      beepFin.play();
    };
  };

  return (
    <div style={{ backgroundColor: "#cbfc00", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", fontFamily: "Arial, sans-serif" }}>
      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.5); }
          70% { box-shadow: 0 0 0 10px rgba(255, 0, 0, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
        }
      `}</style>
      <div style={{ width: "100%", maxWidth: 768, padding: "1rem", display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <div style={{ textAlign: "center" }}>
          <img src="/logo.png" alt="Signal Perú" style={{ height: 80, marginBottom: 12 }} />
          <h1 style={{ fontSize: 26, margin: 0 }}>Signalia</h1>
          <p style={{ fontSize: 18, marginBottom: 6, fontWeight: "bold" }}>
            <strong>Signal te conecta, Signalia te acompaña...</strong>
          </p>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontWeight: "bold", marginRight: 10 }}>Modo de respuesta:</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} style={{ padding: 6, borderRadius: 6, fontSize: 14, background: "#fff", border: "1px solid #888" }}>
              <option value="escolar">Escolar 👶</option>
              <option value="experto">Experto 👨‍💻</option>
            </select>
          </div>
          {escuchando && (
            <div style={{ marginBottom: 12, backgroundColor: "#fff3cd", color: "#856404", padding: "8px 16px", borderRadius: 12, fontWeight: "bold", fontSize: 14 }}>
              🎙️ Signalia te está escuchando...
            </div>
          )}
        </div>
        <div style={{ flexGrow: 1, overflowY: "auto", background: "#ffffcc", padding: 10, borderRadius: 12, marginBottom: 12 }}>
          {messages.map((msg, idx) => (
            <div key={idx} style={{ textAlign: msg.from === "user" ? "right" : "left", marginBottom: 8 }}>
              <span style={{
                display: "inline-block",
                padding: "10px 14px",
                borderRadius: 20,
                backgroundColor: msg.from === "user" ? "#d0eaff" : "#e1ffc7",
                maxWidth: "80%",
                wordBreak: "break-word"
              }}>{msg.text}</span>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={handleVoiceInput} style={{
            backgroundColor: "#fff",
            color: "#111",
            border: "1px solid #888",
            borderRadius: "50%",
            width: 40,
            height: 40,
            fontSize: 18,
            cursor: "pointer",
            animation: escuchando ? "pulse 1.5s infinite" : "none"
          }}>
            🎤
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Escribe tu mensaje..."
            style={{ flexGrow: 1, padding: "10px 14px", borderRadius: 20, border: "1px solid #888", fontSize: 14 }}
          />
          <button onClick={sendMessage} disabled={loading} style={{
            backgroundColor: "#111",
            color: "#fff",
            border: "none",
            borderRadius: "50%",
            width: 40,
            height: 40,
            fontSize: 18,
            cursor: "pointer"
          }}>
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}